<script setup>
import {debounce, get, kebabCase} from 'lodash';
import Pagination from '@/components/Shared/Pagination.vue';
import {computed, onBeforeMount, reactive, ref, watch} from 'vue';
import FormInput from "@/components/Shared/Form/Input/Index.vue";
import VueMultiSelect from "vue-multiselect";
import Popper from 'vue3-popper';
import FormEditor from "@/components/Shared/Form/Editor/Index.vue";
import {useForm} from "@inertiajs/vue3";

const props = defineProps({
  headers: {
    type: Array,
    required: true,
  },
  noDataText: {
    type: String,
    default: 'No results.',
  },
  filters: {
    type: Object,
    default: () => ({}),
  },
  filtersStatus: {
    type: Object,
    default: () => ({}),
  },
  filtersPrio: {
    type: Object,
    default: () => ({}),
  },
  searchPlaceholder: {
    type: String,
    default: null,
  },
  filterFields: {
    type: Object,
    default: null,
  },
  filterFieldsOptions: {
    type: Object,
    default: null,
  },
  filterSelections: {
    type: Array,
    default: [],
  },
  hiddenColumns: {
    type: Array,
    default: null,
  },
  canSelectPerPage: {
    type: Boolean,
    default: false,
  }
});

const emit = defineEmits(['search-changed']);

const editor = ref();
const filterSelectionValue = ref(null);
const loadingTickets = ref(false);
const loadingNotifications = ref(false);

const pageData = reactive({'value': []});
const filters = reactive({});
const filtersStatus = reactive({});
const filtersPrio = reactive({});
const tickets = reactive({});
const notifications = reactive({});

const selectFilterSelection = ref({
  'id': 'filterSelectionsValue',
  'type': 'select',
  'label': 'name',
  'track-by': 'id',
  'options': props.filterSelections
});

const searchInput = ref(null);
const pagination = ref();
const showNotificationPopper = ref(false);

const notificationForm = useForm({});

const filteredHeaders = computed(() => {
  return props.headers.filter(header => !isHidden(header.value));
});

const parseName = text => kebabCase(text);
const textAlignClass = header => ({
  'text-left': header.align === 'start',
  'text-center': header.align === 'center',
  'text-right': header.align === 'end',
});
const onFilterChange = data => {
  const filters = {...pageData.value, ...data};
  const queryParams = {};

  Object.keys(filters).forEach(filter => {
    if (filters[filter] !== null) {
      queryParams[filter] = filters[filter];
    }
  });
  pageData.value = queryParams;

  getEntities(queryParams);
};

const handleSearchChanged = debounce((event) => {
  props.filters.search = event.target.value;
  onFilterChange({search: event.target.value, page: null});
}, 1000);
const handleFilterChanged = debounce((event) => {
  onFilterChange({filter: event, page: null});
}, 1000);
const handleFilterSelectionChanged = debounce((event) => {
  filterSelectionValue.value = event
  resetFilters()
  setFiltersBasedOnSelection(event)
}, 500);
const handleSortChanged = debounce((header) => {
  if (header.sortable) {
    const sort = pageData.value.sort === header.value ? `-${header.value}` : header.value;
    onFilterChange({sort, page: null});
  }
}, 1000);

const handlePerPageChanged = perPage => {
  onFilterChange({perPage, page: 1});
};
const isHidden = header => {
  if (props.hiddenColumns === null) {
    return false;
  }

  return props.hiddenColumns.includes(header);
};

const getItemValue = (item, key) => {
  let value = get(item, key)

  if (value instanceof Object && value.hasOwnProperty('title') && value.hasOwnProperty('subtitle')) {
    return `<span class="d-block">${value.title}</span><small>${value.subtitle}</small>`
  }

  return value
};

const processFiltersForDataRefresh = computed(() => {
  let result = Object.keys(filters).reduce((accumulator, key) => {
    if (filters[key]) {
      accumulator[key] = filters[key];
    }

    return accumulator;
  }, {});

  let filterStatus = processFiltersStatusAndPrio(filtersStatus, 'status')
  let filterPrio = processFiltersStatusAndPrio(filtersPrio, 'prio')

  result['status'] = filterStatus;
  result['priority'] = filterPrio;

  return result;
});

const processFiltersStatusAndPrio = (filterSet, type) => {
  return Object.keys(filterSet).reduce((accumulator, key) => {
    if (filterSet[key]) {
      accumulator.push(key.replace(type, ''));
    }

    return accumulator;
  }, []);
};

const getEntities = (queryParams = []) => {
  loadingTickets.value = true;

  return axios
    .get(route('partner-support.tickets.get-tickets', {...queryParams}))
    .then(({data}) => {
      const {data: entities, ...rest} = data;
      tickets.value = entities;
      pagination.value = rest;
    })
    .finally(() => loadingTickets.value = false);
}

const getNotifications = () => {
  loadingNotifications.value = true;

  return axios
    .get(route('partner-support.tickets.notification.show'))
    .then(({data}) => {
      notifications.value = data;
    })
    .finally(() => loadingNotifications.value = false);
}

const resetFilters = () => {
  let list = [filters, filtersStatus, filtersPrio];
  list.forEach((filter) => {
    for (const [key] of Object.entries(filter)) {
      filter[key] = false;
    }
  })
}

const setFiltersBasedOnSelection = (event) => {
  for (const item of Object.entries(event?.affectedStatuses ?? [])) {
    let value = item[1]
    if (value.includes("status")) {
      filtersStatus[value] = true;
    } else if (value.includes("prio")) {
      filtersPrio[value] = true;
    } else {
      filters[value] = true;
    }
  }
}

const toggleNotificationPopper = () => {
  showNotificationPopper.value = !showNotificationPopper.value
}

const addNotification = () => {
  return axios
    .post(route('partner-support.tickets.notification.create', {...notificationForm}))
    .then(() => {
      getNotifications();
      showNotificationPopper.value = false;
      editor.value.reset();
    }).catch((data) => {
      notificationForm.errors = data.response.data;
    })
};

const showTicket = (ticket, event, blank) => {
  if (event.target.localName === 'a' && !blank) {
    return
  }

  let communicationRoute = route('partner-support.tickets.show-communications', {...ticket})

  if (blank || event.ctrlKey) {
    window.open(communicationRoute, '_blank')
  } else {
    window.open(communicationRoute)
  }
}

watch([filters, filtersStatus, filtersPrio], () => {
  handleFilterChanged(processFiltersForDataRefresh.value)
});

onBeforeMount(() => {
  getEntities()
  getNotifications()
})

</script>

<template>
  <div>
    <div v-for="notification in notifications.value" v-html="notification.message" class="alert alert-warning w-full"
         role="alert"></div>
    <div class="flex checkbox-filter-container">
      <div class="justify-content-start w-full flex">
        <div class="py-2 px-3">
          <div v-for="filterField in filterFieldsOptions.statusOptions" :key="filterField.id">
            <form-input v-model="filtersStatus[filterField.id]" :type="`checkbox`" :label-text="filterField.name"
                        :id="filterField.id" v-bind="{ ...filterField }"/>
          </div>
        </div>
        <div class="py-2 px-3">
          <form-input v-model="filters.unassigned" :type="`checkbox`" :label-text="filterFields.unassigned.name"
                      v-bind="{ ...filterFields.unassigned }"/>
          <form-input v-model="filters.lastAction24Hours" :type="`checkbox`"
                      :label-text="filterFields.lastAction24Hours.name"
                      v-bind="{ ...filterFields.lastAction24Hours }"/>
          <form-input v-model="filters.lastAction48Hours" :type="`checkbox`"
                      :label-text="filterFields.lastAction48Hours.name"
                      v-bind="{ ...filterFields.lastAction48Hours }"/>
        </div>
        <div class="py-2 px-3">
          <form-input v-model="filters.assignedToMe" :type="`checkbox`" :label-text="filterFields.assignedToMe.name"
                      v-bind="{ ...filterFields.assignedToMe }"/>
        </div>
        <div class="py-2 px-3">
          <div v-for="filterField in filterFieldsOptions.priorityOptions" :key="filterField.id" class="mb-2">
            <form-input v-model="filtersPrio[filterField.id]" :type="`checkbox`" :label-text="filterField.name"
                        v-bind="{ ...filterField }"/>
          </div>
        </div>
        <div class="py-2 px-3 gap-2">
          <label class="form_label">Predefined filter selections</label>
          <vue-multi-select
            v-bind="{ ...selectFilterSelection}"
            v-bind:modelValue="filterSelectionValue"
            @update:modelValue="handleFilterSelectionChanged"
          />
        </div>

        <div style="margin-left: auto">
          <Popper :show="showNotificationPopper">
            <button class="btn btn-outline-success" @click="toggleNotificationPopper()">
              Add Notifications
            </button>
            <template #content>
              <span>Add notification</span>
              <div class="py-2 px-3" style="min-width: 400px">
                <form-editor
                  ref="editor"
                  v-model="notificationForm.message"
                  :error="notificationForm.errors?.message"
                  style="background: white"
                  :toolbar="[`link`]"
                ></form-editor>
                <div class="d-flex justify-content-between my-2">
                  <button class="btn btn-secondary" @click="toggleNotificationPopper()">{{ trans('common.close') }}</button>
                  <button class="btn btn-primary" @click="addNotification()">{{ trans('__JSON__.Add') }}</button>
                </div>
              </div>
            </template>
          </Popper>
        </div>
      </div>
    </div>
    <div class="justify-content-end gap w-full flex items-center my-2">
      <input
        ref="searchInput"
        :placeholder="searchPlaceholder || trans('__JSON__.Search')"
        class="form_field"
        type="text"
        @keydown="handleSearchChanged"
      />
      <button type="button" class="btn btn-primary" @click="resetFilters()">
        {{ trans('flows.Reset to default') }}
      </button>
    </div>
    <div>
      <table class="table w-full ticket-table">
        <thead>
        <tr>
          <th
            v-for="(header, index) in filteredHeaders"
            :key="`header-${index}`"
            :class="[textAlignClass(header), header.sortable ? 'cursor-pointer' : '']"
            @click="handleSortChanged(header)"
          >
            <slot :name="parseName(`header-${header.value}`)">
              {{ header.text }}
            </slot>
          </th>
        </tr>
        </thead>
        <tbody>
        <tr
          v-if="!loadingTickets.value"
          v-for="(item, row) in tickets.value"
          :key="`item-row-${row}`"
          @mousedown.middle.prevent
          @click.prevent="showTicket(item, $event)"
          @click.middle.prevent="showTicket(item, $event, true)"
        >
          <td v-for="(header, cell) in filteredHeaders" :key="`item-${row}-${cell}`" :class="textAlignClass(header)">
            <slot :name="parseName(`item-${header.value}`)" v-bind="{ item, index: row }">
              <p v-html="getItemValue(item, header.value)"></p>
            </slot>
          </td>
        </tr>
        <tr v-if="loadingTickets.value" class="text-center">
          <td :colspan="filteredHeaders.length">Loading</td>
        </tr>
        <tr v-else-if="!tickets.value?.length" class="text-center">
          <td :colspan="filteredHeaders.length">{{ noDataText }}</td>
        </tr>
        </tbody>
      </table>
      <pagination
        v-if="pagination"
        :can-select-per-page="canSelectPerPage"
        :pagination="pagination"
        @update:per-page="handlePerPageChanged"
        @paginate="onFilterChange"/>
    </div>
  </div>
</template>

<style>
.form_knob {
  justify-content: start;
}

.form_knob.text_normal {
  font-size: .8rem;
}

.form_knob::before {
  position: relative;
  width: 2rem;
  height: 1rem;
}

.form_knob::after {
  left: 21px;
  height: .8rem;
  width: .8rem;
}

.multiselect__content-wrapper {
  width: fit-content;
}

.notification-card .card-body {
  overflow: auto;
  max-height: 300px;
}

.ticket-table tr td {
  height: 0;
  padding-top: 0;
  padding-bottom: 0;
}

.ticket-table tr:hover {
  cursor: pointer;
}

.ticket-table tr td a {
  margin: 0 auto;
}

</style>
